import { ChevronDown, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { useAgentRoster } from '@/hooks/useAgentRoster';

import { AgentInfoSubmenu } from './AgentInfoSubmenu';

export const MultiAgentSelector = () => {
    const {
        agents,
        selected,
        toggle,
        loading,
        saving,
        hasCheckedSavedSelection,
        resolveDisplayName
    } = useAgentRoster();
    const [isOpen, setIsOpen] = useState(false);
    const [openPanelGuid, setOpenPanelGuid] = useState<string | null>(null);

    useEffect(() => {
        const closePanel = () => setOpenPanelGuid(null);
        window.addEventListener('resize', closePanel);
        return () => window.removeEventListener('resize', closePanel);
    }, []);

    useEffect(() => {
        if (!isOpen) setOpenPanelGuid(null);
    }, [isOpen]);

    const label =
        selected.length === 0 ? 'Select agents' : resolveDisplayName(selected);

    const labelText = selected.length > 1 ? 'Agents' : label;
    const labelCount = selected.length > 1 ? selected.length : null;

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="gap-2 w-full min-w-0 max-w-full text-sm"
                    disabled={saving || !hasCheckedSavedSelection}
                >
                    {saving ? (
                        <Loader2 data-spinner className="size-4 animate-[spin_1s_linear_infinite]" />
                    ) : (
                        <span className="flex flex-1 items-center gap-2 min-w-0 font-medium text-left">
                            <span className="truncate">{labelText}</span>
                            {labelCount !== null && (
                                <span className="inline-flex justify-center items-center border border-border bg-muted/50 group-hover/btn:bg-background group-data-[state=open]/btn:bg-background rounded-full min-w-6 h-6 px-1.5 pt-0.5 font-medium text-foreground text-xs tabular-nums leading-none transition-colors duration-80 shrink-0">
                                    {labelCount}
                                </span>
                            )}
                        </span>
                    )}
                    <ChevronDown
                        className={`transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                    />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="bg-background">
                {loading && (
                    <DropdownMenuItem disabled>
                        <Loader2 className="mr-2 animate-spin" />
                        Loading agents...
                    </DropdownMenuItem>
                )}
                {!loading && agents.length === 0 && (
                    <DropdownMenuItem disabled>
                        No agents available
                    </DropdownMenuItem>
                )}

                <DropdownMenuGroup>
                    {agents.map((agent) => (
                        <AgentInfoSubmenu
                            key={agent.guid}
                            agent={agent}
                            isSelected={selected.includes(agent.agentId)}
                            onToggle={() => toggle(agent.agentId)}
                            open={openPanelGuid === agent.guid}
                            onOpenChange={(open) =>
                                setOpenPanelGuid((current) => {
                                    if (open) return agent.guid;
                                    return current === agent.guid
                                        ? null
                                        : current;
                                })
                            }
                        />
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
